import React from 'react';
import { Invoice, calculateInvoiceTotals, formatCurrency } from '../models/invoice';
import { CompanyDetails as CompanyDetailsType, ClientDetails as ClientDetailsType, InvoiceMeta as InvoiceMetaType } from '../models/invoice';
import logoUrl from '../assets/logo.jpeg';

type HiddenFieldsState = {
    company: { [K in keyof CompanyDetailsType]?: boolean };
    client: { [K in keyof ClientDetailsType]?: boolean };
    meta: { [K in keyof InvoiceMetaType]?: boolean };
};

interface Props {
    invoice: Invoice;
    hiddenFields: HiddenFieldsState;
    companyLogo: string | null; // New prop for user-uploaded logo
}

const PrintInvoice: React.FC<Props> = ({ invoice, hiddenFields, companyLogo }) => {
    if (!invoice) return null;

    const totals = calculateInvoiceTotals(invoice.items || []);

    return (
        <div style={styles.container} className="print-invoice" data-testid="print-invoice-component">

            <div className="invoice-container">
                <div className="logo-area">
                    <img src={companyLogo || logoUrl} alt="Logo" />
                </div>

                <div className="top-row">
                    <div className="buyer-area">
                        <h3>Pirkėjas:</h3>
                        {invoice.client && !hiddenFields.client?.name && <div><strong>{invoice.client.name}</strong></div>}
                        {invoice.client && !hiddenFields.client?.address && <div>{invoice.client.address}</div>}
                        {invoice.client && !hiddenFields.client?.registrationCode && <div>Įm. kodas: {invoice.client.registrationCode}</div>}
                        {invoice.client && !hiddenFields.client?.vatCode && <div>PVM kodas: {invoice.client.vatCode}</div>}
                        {invoice.client && !hiddenFields.client?.email && <div>{invoice.client.email}</div>}
                        {invoice.client && !hiddenFields.client?.phone && <div>{invoice.client.phone}</div>}
                    </div>
                    <div className="meta-area">
                        {invoice.meta && !hiddenFields.meta?.date && <div>Sąskaitos data: {invoice.meta.date}</div>}
                        {invoice.meta && !hiddenFields.meta?.dueDate && <div>Apmokėti iki: {invoice.meta.dueDate}</div>}
                    </div>
                </div>

                <div className="invoice-title">
                    PVM Sąskaita faktūra
                    {invoice.meta && !hiddenFields.meta?.series && !hiddenFields.meta?.number && ` serija ${invoice.meta.series} nr. ${invoice.meta.number}`}
                    {invoice.meta && hiddenFields.meta?.series && !hiddenFields.meta?.number && ` nr. ${invoice.meta.number}`}
                    {invoice.meta && !hiddenFields.meta?.series && hiddenFields.meta?.number && ` serija ${invoice.meta.series}`}
                </div>

                <table>
                    <thead>
                        <tr>
                            <th>Pavadinimas</th>
                            <th className="text-center">Vnt.</th>
                            <th className="text-center">Kiekis</th>
                            <th className="text-right">Kaina</th>
                            <th className="text-right">Viso</th>
                        </tr>
                    </thead>
                    <tbody>
                        {invoice.items.map((item) => (
                            <tr key={item.id}>
                                <td>{item.name}</td>
                                <td className="text-center">{item.unit}</td>
                                <td className="text-center">{item.quantity}</td>
                                <td className="text-right">{formatCurrency(item.price)}</td>
                                <td className="text-right">{formatCurrency(item.price * item.quantity)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="totals-area">
                    <table className="totals-table">
                        <tbody>
                            <tr>
                                <td className="text-right">Viso:</td>
                                <td className="text-right">{formatCurrency(totals.subtotal)}</td>
                            </tr>
                            <tr>
                                <td className="text-right">PVM ({invoice.items.length > 0 ? (invoice.items[0]?.vat || 21) : 21}%):</td>
                                <td className="text-right">{formatCurrency(totals.vatAmount)}</td>
                            </tr>
                            <tr className="total-row">
                                <td className="text-right">Iš viso:</td>
                                <td className="text-right">{formatCurrency(totals.total)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="footer-row">
                    <div>
                        <h3>Pardavėjas:</h3>
                        {invoice.company && !hiddenFields.company?.name && <div><strong>{invoice.company.name}</strong></div>}
                        {invoice.company && !hiddenFields.company?.registrationCode && <div>Įm. kodas: {invoice.company.registrationCode}</div>}
                        {invoice.company && !hiddenFields.company?.vatCode && <div>PVM kodas: {invoice.company.vatCode}</div>}
                    </div>
                    <div>
                        <h3>Kontaktai:</h3>
                        {invoice.company && !hiddenFields.company?.address && <div>{invoice.company.address}</div>}
                        {invoice.company && !hiddenFields.company?.email && <div>{invoice.company.email}</div>}
                        {invoice.company && !hiddenFields.company?.phone && <div>{invoice.company.phone}</div>}
                    </div>
                    <div>
                        <h3>Banko Rekvizitai:</h3>
                        {invoice.company && !hiddenFields.company?.bankName && <div>{invoice.company.bankName}</div>}
                        {invoice.company && !hiddenFields.company?.bankAccount && <div>IBAN: {invoice.company.bankAccount}</div>}
                        {invoice.company && !hiddenFields.company?.swift && <div>SWIFT: {invoice.company.swift}</div>}
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    container: {
        fontFamily: 'Arial, sans-serif',
        color: '#000',
        lineHeight: 1.5,
    },
};



export default PrintInvoice;
